import { FC, useCallback, useEffect, useRef, useState } from "react";
import { ChevronDown, ChevronLeft, ChevronRight, ChevronUp } from "lucide-react";
import { Select } from "antd";
import { AutoModel } from "./AutoModel";
import { useShellActions } from "./ShellActionsContext";
import { globalStore } from "../../utils/globalStore";
import {
  getPointsIncomeRecordsApi,
  getPointsExpenditureRecordsApi,
  type PersonalIncomeLogItem,
  type PersonalExpenditureLogItem,
} from "../../service/points";
import {
  getMembershipCurrentApi,
  type MembershipCurrentRespVO,
} from "../../service/membership";
import "./PointsLedgerModal.scss";

export interface PointsLedgerModalProps {
  open: boolean;
  onClose: () => void;
  /** 顶部余额卡当前可用积分 fallback；接口返回后会优先用接口值 */
  availablePoints?: number;
  /** 顶部余额卡会员积分 fallback；接口返回后会优先用接口值 */
  memberPoints?: number;
  /** 顶部余额卡积分包余额 fallback；接口返回后会优先用接口值 */
  packBalance?: number;
  /** 是否付费用户 fallback；接口返回后会优先用接口值（membershipId > 0 视为付费） */
  isPaid?: boolean;
}

/** 积分类型 key（接口 status：member / package / gift；展示为 会员积分 / 积分包 / 赠送积分） */
type EarnTypeKey = "member" | "package" | "gift";

interface EarnRecord {
  id: string;
  time: string;
  source: string;
  type: string;
  typeKey: EarnTypeKey;
  typeClass: string;
  expiry: string;
  amount: number;
}

type SpendStatusKey = "consumed" | "returned" | "frozen";

interface SpendRecord {
  id: string;
  time: string;
  createdAt: string;
  feature: string;
  task: string;
  /** 后端原始扣减来源 key（member/permanent/mixed），表格「抵扣来源」列直接展示此值 */
  deductionSource: string;
  amount: number;
  memberPoint: number;
  permanentPoint: number;
  status: string;
  statusKey: SpendStatusKey;
  pointResult: string;
  reason: string;
  /** 积分流水号（用于详情「积分流水号」+ 复制按钮） */
  flowId: string;
  /** 业务任务 ID（用于详情「业务任务 ID」） */
  taskId: string;
  /** 计费单号（暂未在 UI 渲染，预留） */
  billingNo: string;
}

const EARN_PAGE_SIZE = 5;
const SPEND_PAGE_SIZE = 5;

const EARN_FILTER_OPTIONS: { value: EarnTypeKey | "all"; label: string }[] = [
  { value: "all", label: "全部类型" },
  { value: "member", label: "会员积分" },
  { value: "package", label: "积分包" },
  { value: "gift", label: "赠送积分" },
];

const SPEND_FILTER_OPTIONS: { value: SpendStatusKey | "all"; label: string }[] = [
  { value: "all", label: "全部" },
  { value: "consumed", label: "已消费" },
  { value: "returned", label: "已退还" },
  { value: "frozen", label: "冻结" },
];

/** 与 SPEND_FILTER_OPTIONS 同步：用于表格与详情面板渲染中文标签（不再直接展示接口原始 status） */
const SPEND_STATUS_LABELS: Record<SpendStatusKey, string> = {
  consumed: "已消费",
  returned: "已退还",
  frozen: "冻结",
};

const getSpendStatusLabel = (statusKey: SpendStatusKey): string =>
  SPEND_STATUS_LABELS[statusKey] ?? "—";

/** 把 pointType 字符串映射到 doc 的 typeKey / typeClass */
function deriveEarnTypeClass(pointType: string): { type: string; typeKey: EarnTypeKey; typeClass: string } {
  const t = String(pointType || "").trim();
  if (/会员|member/i.test(t)) {
    return { type: t || "会员积分", typeKey: "member", typeClass: "member" };
  }
  if (/包|pack|充值/i.test(t)) {
    return { type: t || "积分包", typeKey: "package", typeClass: "package" };
  }
  if (/赠送|gift|bonus|奖励|活动/i.test(t)) {
    return { type: t || "赠送积分", typeKey: "gift", typeClass: "gift" };
  }
  return { type: t || "其他", typeKey: "member", typeClass: "member" };
}

/** 把后端 status 字符串映射到 doc 的 statusKey */
function deriveSpendStatusKey(status: string): SpendStatusKey {
  const s = String(status || "").trim();
  if (/退|return|refund/i.test(s)) return "returned";
  if (/冻结|frozen|freeze/i.test(s)) return "frozen";
  return "consumed";
}

function deriveSpendPointResult(statusKey: SpendStatusKey, amount: number): string {
  const absAmount = Math.abs(amount);
  switch (statusKey) {
    case "consumed":
      return `已消费 ${absAmount} 积分`;
    case "returned":
      return `已退还 ${absAmount} 积分`;
    case "frozen":
      return `冻结 ${absAmount} 积分`;
    default:
      return "—";
  }
}

function mapIncomeRecord(raw: PersonalIncomeLogItem, index: number): EarnRecord {
  const derived = deriveEarnTypeClass(raw.pointType);
  return {
    id: String(raw.id ?? `${raw.createTime}-${index}`),
    time: raw.createTime,
    source: raw.source,
    type: derived.type,
    typeKey: derived.typeKey,
    typeClass: derived.typeClass,
    expiry: raw.expireTime,
    amount: Number(raw.pointsIncome) || 0,
  };
}

function mapExpenditureRecord(raw: PersonalExpenditureLogItem, index: number): SpendRecord {
  const amount = Number(raw.expenditurePoints) || 0;
  const statusKey = deriveSpendStatusKey(raw.status);
  const status = raw.status || "已完成";
  // 优先用后端下发的展示名（deductionSource，如「积分钱包」），回落到 pointSource 枚举值（member/permanent/mixed）
  const deductionSource = String(raw.deductionSource ?? raw.pointSource ?? "");
  return {
    // serialId 在「已退还 / 冻结」成对流水里可能重复（同一个 voice-clone-* 任务会同时下两条），
    // 必须把 status 也拼进去，否则 React key 重复会把两条 reconcile 成一条、错位展开
    id: `${raw.serialId ?? `${raw.createTime}-${index}`}-${statusKey}`,
    time: raw.createTime,
    createdAt: raw.createTime,
    feature: raw.functionType,
    task: raw.taskName,
    deductionSource,
    amount,
    memberPoint: Number(raw.memberPoint) || 0,
    permanentPoint: Number(raw.permanentPoint) || 0,
    status,
    statusKey,
    pointResult: deriveSpendPointResult(statusKey, amount),
    reason: statusKey === "returned" ? (raw.failReason || "—") : "—",
    flowId: raw.recordNo ?? raw.serialId ?? "",
    taskId: raw.businessTaskId ?? "",
    billingNo: raw.billingNo ?? "",
  };
}

const resolveUserId = (): number | null => {
  const u = (globalStore.getUserInfo() as { id?: number } | null)?.id;
  return typeof u === "number" && u > 0 ? u : null;
};

export const PointsLedgerModal: FC<PointsLedgerModalProps> = ({
  open,
  onClose,
  availablePoints: availablePointsProp,
  memberPoints: memberPointsProp,
  packBalance: packBalanceProp,
  isPaid: isPaidProp = true,
}) => {
  const actions = useShellActions();
  const [activeTab, setActiveTab] = useState<"earn" | "spend">("earn");
  const [earnFilter, setEarnFilter] = useState<EarnTypeKey | "all">("all");
  const [earnSort, setEarnSort] = useState<"asc" | "desc">("desc");
  const [earnPage, setEarnPage] = useState(1);
  const [spendFilter, setSpendFilter] = useState<SpendStatusKey | "all">("all");
  const [spendSort, setSpendSort] = useState<"asc" | "desc">("desc");
  const [spendPage, setSpendPage] = useState(1);
  const [expandedSpendId, setExpandedSpendId] = useState<string | null>(null);
  const [copiedFlowId, setCopiedFlowId] = useState<string | null>(null);

  const [earnRecords, setEarnRecords] = useState<EarnRecord[]>([]);
  const [spendRecords, setSpendRecords] = useState<SpendRecord[]>([]);
  const [earnTotal, setEarnTotal] = useState(0);
  const [spendTotal, setSpendTotal] = useState(0);

  // 用 ref 跟踪 earn/spend 最近一次请求的 token；只有当前 token 的响应才允许写入 state，避免旧响应覆盖新数据
  const earnRequestTokenRef = useRef(0);
  const spendRequestTokenRef = useRef(0);
  /** /video/membership/current：顶部余额卡的真实数据源；未返回时回落到 props */
  const [currentMembership, setCurrentMembership] = useState<MembershipCurrentRespVO | null>(null);

  /** 顶部余额卡展示值：优先使用 /video/membership/current 接口返回，回落到 props */
  const displayAvailablePoints = currentMembership?.availablePoint ?? availablePointsProp ?? 0;
  const displayMemberPoints = currentMembership?.memberAvailablePoint ?? memberPointsProp;
  const displayPackBalance = currentMembership?.permanentAvailablePoint ?? packBalanceProp;
  /** 付费用户判定：membershipId > 0 表示有生效会员订阅；否则回落到 prop */
  const displayIsPaid =
    typeof currentMembership?.membershipId === "number" && currentMembership.membershipId > 0
      ? true
      : isPaidProp;

  /** 拉取当前 tab 真实数据；空 / 报错直接置空，由表格空态渲染
  - 真实分页：pageSize=5，pageNo 由调用方传入，total 用后端返回的总数
  - 每个请求分配递增 token，仅最新 token 的响应允许写入 state，避免旧响应污染 */
  const fetchTabData = useCallback(
    async (
      tab: "earn" | "spend",
      opts?: { status?: string; timeOrder?: "asc" | "desc"; pageNo?: number }
    ) => {
      const userId = resolveUserId();
      const pageNo = Math.max(1, opts?.pageNo ?? 1);
      const tokenRef = tab === "earn" ? earnRequestTokenRef : spendRequestTokenRef;
      const myToken = tokenRef.current + 1;
      tokenRef.current = myToken;
      if (!userId) {
        if (myToken !== tokenRef.current) return;
        if (tab === "earn") {
          setEarnRecords([]);
          setEarnTotal(0);
        } else {
          setSpendRecords([]);
          setSpendTotal(0);
        }
        return;
      }
      try {
        if (tab === "earn") {
          const res = await getPointsIncomeRecordsApi({
            pageNo,
            pageSize: EARN_PAGE_SIZE,
            userId,
            status: opts?.status,
            timeOrder: opts?.timeOrder,
          });
          if (myToken !== tokenRef.current) return; // 过期响应，丢弃
          const list = Array.isArray(res?.data?.list) ? res.data.list : [];
          const mapped = list.map(mapIncomeRecord).slice(0, EARN_PAGE_SIZE);
          setEarnRecords(mapped);
          setEarnTotal(Number(res.data?.total ?? mapped.length));
        } else {
          const res = await getPointsExpenditureRecordsApi({
            pageNo,
            pageSize: SPEND_PAGE_SIZE,
            userId,
            status: opts?.status,
            timeOrder: opts?.timeOrder,
          });
          if (myToken !== tokenRef.current) return; // 过期响应，丢弃
          const list = Array.isArray(res?.data?.list) ? res.data.list : [];
          const mapped = list.map(mapExpenditureRecord).slice(0, SPEND_PAGE_SIZE);
          setSpendRecords(mapped);
          setSpendTotal(Number(res.data?.total ?? mapped.length));
        }
      } catch {
        if (myToken !== tokenRef.current) return; // 过期错误，丢弃
        if (tab === "earn") {
          setEarnRecords([]);
          setEarnTotal(0);
        } else {
          setSpendRecords([]);
          setSpendTotal(0);
        }
      }
    },
    []
  );

  useEffect(() => {
    if (open) {
      // 静默打开：不预先清空，保留旧的列表/分页位，等新接口回来再整体替换
      setEarnPage(1);
      setSpendPage(1);
      setExpandedSpendId(null);
      void fetchTabData("earn", {
        status: earnFilter !== "all" ? earnFilter : undefined,
        timeOrder: earnSort,
        pageNo: 1,
      });
      void fetchTabData("spend", {
        status: spendFilter !== "all" ? spendFilter : undefined,
        timeOrder: spendSort,
        pageNo: 1,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // 顶部余额卡：每次打开调用 /video/membership/current 拿真实数据；失败保持 props
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setCurrentMembership(null);
    getMembershipCurrentApi()
      .then((res) => {
        if (cancelled) return;
        if (res?.code === 0 && res.data) {
          setCurrentMembership(res.data);
        }
      })
      .catch(() => {
        /* 静默失败：保持 props 回退 */
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  // 积分获取筛选 / 排序变化时按服务端 status + timeOrder 重拉
  // 静默切换：保留旧记录避免闪烁，等新接口回来再整体替换
  useEffect(() => {
    if (!open) return;
    setEarnPage(1);
    void fetchTabData("earn", {
      status: earnFilter !== "all" ? earnFilter : undefined,
      timeOrder: earnSort,
      pageNo: 1,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [earnFilter, earnSort]);

  // 积分消耗筛选 / 排序变化时按服务端 status + timeOrder 重拉
  // 静默切换：保留旧记录避免闪烁，等新接口回来再整体替换
  useEffect(() => {
    if (!open) return;
    setSpendPage(1);
    void fetchTabData("spend", {
      status: spendFilter !== "all" ? spendFilter : undefined,
      timeOrder: spendSort,
      pageNo: 1,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spendFilter, spendSort]);

  // earn 翻页时按服务端 pageNo 重拉
  // 静默切换：不预先清空，等新接口回来再整体替换 spendRecords，避免表格一闪一闪
  useEffect(() => {
    if (!open) return;
    void fetchTabData("earn", {
      status: earnFilter !== "all" ? earnFilter : undefined,
      timeOrder: earnSort,
      pageNo: earnPage,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [earnPage]);

  // spend 翻页时按服务端 pageNo 重拉
  // 静默切换：不预先清空，等新接口回来再整体替换 spendRecords，避免表格一闪一闪
  useEffect(() => {
    if (!open) return;
    void fetchTabData("spend", {
      status: spendFilter !== "all" ? spendFilter : undefined,
      timeOrder: spendSort,
      pageNo: spendPage,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spendPage]);

  // 接口已按 pageSize 切片返回当前页数据，earnRecords / spendRecords 直接就是当前页条目；
  // 仅兜底裁剪到 pageSize，避免最后一页不满时残留下一页痕迹。
  // 不做客户端排序：服务端 timeOrder 已经按升/降序返回，表格直接照服务端顺序展示

  // totalPages 改用后端真实 total 计算
  const earnTotalPages = Math.max(1, Math.ceil(earnTotal / EARN_PAGE_SIZE));
  const spendTotalPages = Math.max(1, Math.ceil(spendTotal / SPEND_PAGE_SIZE));

  const safeEarnPage = Math.min(earnPage, earnTotalPages);
  const safeSpendPage = Math.min(spendPage, spendTotalPages);

  const visibleEarn = earnRecords.slice(0, EARN_PAGE_SIZE);
  const visibleSpend = spendRecords.slice(0, SPEND_PAGE_SIZE);

  const earnRange = visibleEarn.length
    ? `显示 ${(safeEarnPage - 1) * EARN_PAGE_SIZE + 1}–${(safeEarnPage - 1) * EARN_PAGE_SIZE + visibleEarn.length} 条，共 ${earnTotal} 条`
    : `共 ${earnTotal} 条记录`;
  const spendRange = visibleSpend.length
    ? `显示 ${(safeSpendPage - 1) * SPEND_PAGE_SIZE + 1}–${(safeSpendPage - 1) * SPEND_PAGE_SIZE + visibleSpend.length} 条，共 ${spendTotal} 条`
    : `共 ${spendTotal} 条记录`;

  const toggleSpendRow = useCallback((id: string) => {
    setExpandedSpendId((prev) => (prev === id ? null : id));
  }, []);

  const handleCopyFlowId = useCallback(async (flowId: string) => {
    if (!flowId) return;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(flowId);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = flowId;
        textarea.setAttribute("readonly", "");
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.select();
        try {
          document.execCommand("copy");
        } catch {
          /* noop */
        }
        document.body.removeChild(textarea);
      }
      setCopiedFlowId(flowId);
      window.setTimeout(() => {
        setCopiedFlowId((cur) => (cur === flowId ? null : cur));
      }, 1200);
    } catch {
      /* noop */
    }
  }, []);

  const handleBuyPoints = useCallback(() => {
    onClose();
    actions?.openPurchase();
  }, [actions, onClose]);

  const handleEarnPageChange = useCallback((delta: number) => {
    setEarnPage((p) => Math.max(1, Math.min(earnTotalPages, p + delta)));
  }, [earnTotalPages]);

  const handleSpendPageChange = useCallback((delta: number) => {
    setSpendPage((p) => Math.max(1, Math.min(spendTotalPages, p + delta)));
  }, [spendTotalPages]);

  const formatAmount = (n: number) => (n > 0 ? `+${n}` : String(n));
  const amountClass = (n: number) => (n < 0 ? "negative" : n > 0 ? "positive" : "neutral");

  return (
    <AutoModel
      open={open}
      onClose={onClose}
      title={
        <div>
          <h2>积分明细</h2>
          <p>查看积分获取、消耗去向和有效期</p>
        </div>
      }
      size="xxl"
      contentMaxWidth={880}
      contentClassName="points-ledger-shell"
      overlayClassName="points-ledger-modal-overlay"
      footerRight={null}
    >
      <div className="points-ledger-body">
        <div className="points-ledger-balance">
          <div>
            <span>当前可用积分</span>
            <strong>{displayAvailablePoints.toLocaleString("zh-CN")}</strong>
          </div>
          <div>
            <span>会员积分</span>
            <strong>{displayMemberPoints == null ? "—" : displayMemberPoints.toLocaleString("zh-CN")}</strong>
            <small>本周期结束后清零</small>
          </div>
          <div>
            <span>积分包余额</span>
            <strong>{displayPackBalance == null ? "—" : displayPackBalance.toLocaleString("zh-CN")}</strong>
            <small>按最早到期顺序抵扣</small>
          </div>
          {displayIsPaid ? (
            <button type="button" onClick={handleBuyPoints}>
              购买积分包
            </button>
          ) : null}
        </div>

        <div className="points-ledger-tabs" role="tablist" aria-label="积分记录类型">
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "earn"}
            className={activeTab === "earn" ? "active" : ""}
            onClick={() => setActiveTab("earn")}
          >
            积分获取
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "spend"}
            className={activeTab === "spend" ? "active" : ""}
            onClick={() => setActiveTab("spend")}
          >
            积分消耗
          </button>
        </div>

        <div className="points-ledger-content">
          {/* 积分获取 */}
          {activeTab === "earn" ? (
            <>
              <div className="points-ledger-toolbar">
                <span>共 {earnTotal} 条记录</span>
                <div className="points-ledger-toolbar__filters">
                  <Select
                    className="points-ledger-toolbar__select points-ledger-toolbar__select--doc"
                    popupClassName="points-ledger-toolbar__select-dropdown"
                    value={earnFilter}
                    onChange={(value) => {
                      setEarnFilter(value as EarnTypeKey | "all");
                      setEarnPage(1);
                    }}
                    options={EARN_FILTER_OPTIONS.map((opt) => ({
                      value: opt.value,
                      label: opt.label,
                    }))}
                    getPopupContainer={(triggerNode) => triggerNode.parentElement || document.body}
                  />
                  <Select
                    className="points-ledger-toolbar__select points-ledger-toolbar__select--doc"
                    popupClassName="points-ledger-toolbar__select-dropdown"
                    value={earnSort}
                    onChange={(value) => {
                      setEarnSort(value as "asc" | "desc");
                      setEarnPage(1);
                    }}
                    options={[
                      { value: "desc", label: "时间降序" },
                      { value: "asc", label: "时间升序" },
                    ]}
                    getPopupContainer={(triggerNode) => triggerNode.parentElement || document.body}
                  />
                </div>
              </div>
              <table>
                <thead>
                  <tr>
                    <th>获取时间</th>
                    <th>积分来源</th>
                    <th>积分类型</th>
                    <th>有效期</th>
                    <th className="number">积分</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleEarn.length === 0 ? (
                    <tr>
                      <td className="points-ledger-empty" colSpan={5}>
                        暂无符合条件的积分获取记录
                      </td>
                    </tr>
                  ) : (
                    visibleEarn.map((r) => (
                      <tr key={r.id}>
                        <td>{r.time}</td>
                        <td>{r.source}</td>
                        <td>
                          <span className={`tag ${r.typeClass}`}>{r.type}</span>
                        </td>
                        <td>{r.expiry || "—"}</td>
                        <td className="number positive">+{r.amount.toLocaleString("zh-CN")}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
              <div className="points-ledger-pagination">
                <span>{earnRange}</span>
                <div>
                  <button
                    type="button"
                    aria-label="上一页"
                    onClick={() => handleEarnPageChange(-1)}
                    // 上一页可用：当前页之前的累计条目数（含当前页）> 0
                    disabled={(safeEarnPage - 1) * EARN_PAGE_SIZE === 0}
                  >
                    <ChevronLeft size={14} strokeWidth={2.4} aria-hidden />
                  </button>
                  <strong>
                    {safeEarnPage} / {earnTotalPages}
                  </strong>
                  <button
                    type="button"
                    aria-label="下一页"
                    onClick={() => handleEarnPageChange(1)}
                    // 下一页可用：当前页之前的累计条目数（含当前页）< 真实总数
                    disabled={safeEarnPage * EARN_PAGE_SIZE >= earnTotal}
                  >
                    <ChevronRight size={14} strokeWidth={2.4} aria-hidden />
                  </button>
                </div>
              </div>
            </>
          ) : null}

          {/* 积分消耗 */}
          {activeTab === "spend" ? (
            <>
              <div className="points-ledger-toolbar">
                <span>共 {spendTotal} 条记录</span>
                <div className="points-ledger-toolbar__filters">
                  <Select
                    className="points-ledger-toolbar__select points-ledger-toolbar__select--doc"
                    popupClassName="points-ledger-toolbar__select-dropdown"
                    value={spendFilter}
                    onChange={(value) => {
                      setSpendFilter(value as SpendStatusKey | "all");
                      setSpendPage(1);
                    }}
                    options={SPEND_FILTER_OPTIONS.map((opt) => ({
                      value: opt.value,
                      label: opt.label,
                    }))}
                    getPopupContainer={(triggerNode) => triggerNode.parentElement || document.body}
                  />
                  <Select
                    className="points-ledger-toolbar__select points-ledger-toolbar__select--doc"
                    popupClassName="points-ledger-toolbar__select-dropdown"
                    value={spendSort}
                    onChange={(value) => {
                      setSpendSort(value as "asc" | "desc");
                      setSpendPage(1);
                    }}
                    options={[
                      { value: "desc", label: "时间降序" },
                      { value: "asc", label: "时间升序" },
                    ]}
                    getPopupContainer={(triggerNode) => triggerNode.parentElement || document.body}
                  />
                </div>
              </div>
              <table className="points-ledger-spend-table">
                <thead>
                  <tr>
                    <th>消耗时间</th>
                    <th>功能类型</th>
                    <th>任务名称</th>
                    <th>抵扣来源</th>
                    <th>状态</th>
                    <th className="number">积分</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleSpend.length === 0 ? (
                    <tr>
                      <td className="points-ledger-empty" colSpan={6}>
                        暂无符合条件的积分消耗记录
                      </td>
                    </tr>
                  ) : (
                    visibleSpend.map((r) => {
                      const expanded = expandedSpendId === r.id;
                      return (
                        <RowWithDetail
                          key={r.id}
                          record={r}
                          expanded={expanded}
                          onToggle={() => toggleSpendRow(r.id)}
                          amountText={formatAmount(r.amount)}
                          amountClass={amountClass(r.amount)}
                          copied={copiedFlowId === r.flowId}
                          onCopy={() => handleCopyFlowId(r.flowId)}
                        />
                      );
                    })
                  )}
                </tbody>
              </table>
              <div className="points-ledger-pagination">
                <span>{spendRange}</span>
                <div>
                  <button
                    type="button"
                    aria-label="上一页"
                    onClick={() => handleSpendPageChange(-1)}
                    // 上一页可用：当前页之前的累计条目数（含当前页）> 0
                    disabled={(safeSpendPage - 1) * SPEND_PAGE_SIZE === 0}
                  >
                    <ChevronLeft size={14} strokeWidth={2.4} aria-hidden />
                  </button>
                  <strong>
                    {safeSpendPage} / {spendTotalPages}
                  </strong>
                  <button
                    type="button"
                    aria-label="下一页"
                    onClick={() => handleSpendPageChange(1)}
                    // 下一页可用：当前页之前的累计条目数（含当前页）< 真实总数
                    disabled={safeSpendPage * SPEND_PAGE_SIZE >= spendTotal}
                  >
                    <ChevronRight size={14} strokeWidth={2.4} aria-hidden />
                  </button>
                </div>
              </div>
            </>
          ) : null}
        </div>

        <div className="points-ledger-foot">
          <span>积分按最早到期顺序抵扣；失败任务请展开记录查看积分处理结果。</span>
        </div>
      </div>
    </AutoModel>
  );
};

interface RowWithDetailProps {
  record: SpendRecord;
  expanded: boolean;
  onToggle: () => void;
  amountText: string;
  amountClass: string;
  copied: boolean;
  onCopy: () => void;
}

const RowWithDetail: FC<RowWithDetailProps> = ({
  record,
  expanded,
  onToggle,
  amountText,
  amountClass,
  copied,
  onCopy,
}) => {
  return (
    <>
      <tr
        className={`points-ledger-row${expanded ? " is-open" : ""}`}
        data-ledger-row={record.id}
        tabIndex={0}
        aria-expanded={expanded ? "true" : "false"}
        onClick={onToggle}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onToggle();
          }
        }}
      >
        <td>{record.time}</td>
        <td>{record.feature}</td>
        <td>{record.task}</td>
        <td>{record.deductionSource || "—"}</td>
        <td>
          <span className={`points-ledger-status ${record.statusKey}`}>{getSpendStatusLabel(record.statusKey)}</span>
        </td>
        <td className="number">
          <div className="points-ledger-value-cell">
            <span className={amountClass}>{amountText}</span>
            <button
              type="button"
              className="points-ledger-row-toggle"
              aria-label={expanded ? "收起积分记录详情" : "展开积分记录详情"}
              onClick={(e) => {
                e.stopPropagation();
                onToggle();
              }}
            >
              {expanded ? <ChevronUp size={14} strokeWidth={2.4} aria-hidden /> : <ChevronDown size={14} strokeWidth={2.4} aria-hidden />}
            </button>
          </div>
        </td>
      </tr>
      <tr
        className="points-ledger-detail-row"
        data-ledger-detail={record.id}
        hidden={!expanded}
      >
        <td colSpan={6}>
          <div className="points-ledger-detail">
            <div className="points-ledger-detail-item">
              <span>积分流水号</span>
              <div>
                <strong>{record.flowId || "—"}</strong>
                <button
                  type="button"
                  className={copied ? "copied" : ""}
                  onClick={onCopy}
                >
                  {copied ? "已复制" : "复制"}
                </button>
              </div>
            </div>
            <div className="points-ledger-detail-item">
              <span>业务任务 ID</span>
              <strong>{record.taskId || "—"}</strong>
            </div>
            <div className="points-ledger-detail-item">
              <span>扣减来源</span>
              <strong>{record.deductionSource || "—"}</strong>
            </div>
            <div className="points-ledger-detail-item">
              <span>处理状态</span>
              <strong>{getSpendStatusLabel(record.statusKey)}</strong>
            </div>
            <div className="points-ledger-detail-item">
              <span>积分处理</span>
              <strong>{record.pointResult}</strong>
            </div>
            <div className="points-ledger-detail-item">
              <span>创建时间</span>
              <strong>{record.createdAt || "—"}</strong>
            </div>
            <div className="points-ledger-detail-item wide">
              <span>异常原因</span>
              <strong>{record.reason || "—"}</strong>
            </div>
          </div>
        </td>
      </tr>
    </>
  );
};